// Simple, accessible modal with Turbo-friendly hooks
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["container", "panel", "tab", "pane"]

  connect() {
    // If nothing is selected, activate the first tab
    let activeTab = this.tabTargets.find(t => t.getAttribute("aria-selected") === "true")
    if (!activeTab) activeTab = this.tabTargets[0]
    if (activeTab) {
      this._activateTab(activeTab)
      this._showPane(activeTab.dataset.name)
    }
  }

  open(event) {
    event?.preventDefault()
    this.containerTarget.classList.remove("hidden")
    this.containerTarget.classList.add("flex")
    this._trapFocus()
    this.panelTarget.querySelector("button,[href],input,select,textarea,[tabindex]:not([tabindex='-1'])")?.focus()
  }

  close(event) {
    event?.preventDefault()
    this.containerTarget.classList.add("hidden")
    this.containerTarget.classList.remove("flex")
    this._releaseFocus()
  }

  backdropClose(event) {
    if (event.target === this.containerTarget) this.close()
  }

  escClose(event) {
    if (event.key === "Escape") this.close()
  }

  switchTab(event) {
    const btn = event.currentTarget
    this._activateTab(btn)
    this._showPane(btn.dataset.name)
  }

  // ----- helpers -----
  _activateTab(activeBtn) {
    const ACTIVE = ["border-yellow-700", "text-slate-900"]
    const INACTIVE = ["border-transparent", "text-gray-500", "hover:text-gray-700", "hover:border-gray-300"]

    this.tabTargets.forEach(t => {
      const isActive = t === activeBtn
      t.setAttribute("aria-selected", isActive ? "true" : "false")
      t.tabIndex = isActive ? 0 : -1

      // reset both sets, then apply only what we need
      ACTIVE.forEach(c => t.classList.remove(c))
      INACTIVE.forEach(c => t.classList.remove(c))
      ;(isActive ? ACTIVE : INACTIVE).forEach(c => t.classList.add(c))
    })
  }

  _showPane(name) {
    this.paneTargets.forEach(p => p.classList.toggle("hidden", p.dataset.name !== name))
  }

  // --- focus trap (basic) ---
  _trapFocus() {
    this._onKeydown = (e) => {
      if (e.key !== "Tab") return
      const focusables = this._focusables()
      if (focusables.length === 0) return
      const first = focusables[0]
      const last  = focusables[focusables.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus()
      }
    }
    document.addEventListener("keydown", this._onKeydown)
  }

  _releaseFocus() {
    document.removeEventListener("keydown", this._onKeydown)
  }

  _focusables() {
    return [...this.panelTarget.querySelectorAll(
      "a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex='-1'])"
    )].filter(el => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"))
  }
}
